// components/ProjectMemberRoleManager.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, Shield, Edit2, Save, X, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

interface ProjectMemberRoleManagerProps {
  projectId: string;
  userId: string;
  memberName: string;
  memberEmail: string;
  memberGlobalRole?: string | null;
  isCreator: boolean; // Si true, peut modifier tous les rôles. Si false, peut seulement modifier son propre rôle
  currentUserId?: string; // ID de l'utilisateur actuellement connecté
  initialProjectRole?: string | null; // Rôle préchargé par le parent — évite un GET par membre
  onUpdate?: () => void;
}

const ProjectMemberRoleManager: React.FC<ProjectMemberRoleManagerProps> = ({
  projectId,
  userId,
  memberName,
  memberEmail,
  memberGlobalRole,
  isCreator,
  currentUserId,
  initialProjectRole,
  onUpdate
}) => {
  // Vérifier si l'utilisateur peut modifier ce rôle
  // - Le créateur peut modifier tous les rôles
  // - Un membre peut modifier uniquement son propre rôle
  const canEdit = isCreator || (currentUserId && currentUserId === userId);
  const [projectRole, setProjectRole] = useState<string>(initialProjectRole ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(initialProjectRole === undefined);
  const [suggestedRoles] = useState<string[]>([
    'Décideur',
    'Coordinateur',
    'Chercheur',
    'Partenaire',
    'Observateur',
    'Ministre',
    'Politique',
    'Expert Technique'
  ]);

  useEffect(() => {
    if (initialProjectRole !== undefined) {
      setProjectRole(initialProjectRole ?? '');
      setFetching(false);
      return;
    }
    fetchProjectRole();
  }, [projectId, userId, initialProjectRole]);

  const fetchProjectRole = async () => {
    setFetching(true);
    try {
      const res = await axios.get(
        `${API_BASE}/projects/${projectId}/members/${userId}/role`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data.success && res.data.data) {
        setProjectRole(res.data.data.role || '');
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Erreur récupération rôle projet:', err);
      }
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!projectRole.trim()) {
      alert('Veuillez entrer un rôle');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/projects/${projectId}/members/${userId}/role`,
        { role: projectRole.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Erreur assignation rôle:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'assignation du rôle');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle de ${memberName} ?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(
        `${API_BASE}/projects/${projectId}/members/${userId}/role`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setProjectRole('');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Erreur suppression rôle:', err);
      alert(err.response?.data?.error || 'Erreur lors de la suppression du rôle');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    fetchProjectRole();
    setIsEditing(false);
  };

  // Si l'utilisateur ne peut pas modifier (ni créateur, ni propriétaire du rôle)
  if (!canEdit) {
    // Afficher seulement le rôle en lecture seule
    if (fetching) {
      return <div className="text-sm text-gray-500">Chargement...</div>;
    }
    return (
      <div className="flex items-center gap-2">
        {projectRole ? (
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
            {projectRole}
          </span>
        ) : (
          <span className="text-xs text-gray-500">Aucun rôle assigné</span>
        )}
      </div>
    );
  }

  if (fetching) {
    return <div className="text-sm text-gray-500">Chargement...</div>;
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-900">{memberName}</span>
            {memberGlobalRole && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                {memberGlobalRole}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">{memberEmail}</p>
        </div>
        {!isEditing && (
          <div className="flex items-center gap-2">
            {/* Seul le créateur peut supprimer un rôle */}
            {projectRole && isCreator && (
              <button
                onClick={handleRemove}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Supprimer le rôle"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Modifier le rôle"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle dans ce projet
            </label>
            <input
              type="text"
              value={projectRole}
              onChange={(e) => setProjectRole(e.target.value)}
              placeholder="Ex: Décideur, Coordinateur, Chercheur..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              list={`suggested-roles-${userId}`}
            />
            <datalist id={`suggested-roles-${userId}`}>
              {suggestedRoles.map((role) => (
                <option key={role} value={role} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-gray-500">
              Ce rôle détermine les permissions dans ce projet spécifique
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              <Save className="h-3 w-3" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm"
            >
              <X className="h-3 w-3" />
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div>
          {projectRole ? (
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-purple-600" />
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                {projectRole}
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No role assigned. Click on "Edit" to assign a role.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectMemberRoleManager;
